<template>
  <div
    v-if="notification"
    class="notification-message"
  >
    <div
      v-if="notification?.data?.html_message"
      class="notification-content"
      v-html="notification?.data?.html_message"
    />
    <div
      v-if="notification?.data?.message"
      class="notification-content"
    >
      <div
        v-if="notification?.data?.message?.title != notification?.title"
        class="notification-message-title"
      >
        {{ notification?.data?.message?.title }}
      </div>
      <div
        v-if="notification?.data?.message?.actions"
        class="notification-actions"
      >
        <button
          v-for="action in notification?.data?.message?.actions"
          :key="action.title"
          class="notification-action-btn"
          @click="handleAction(notification, action)"
        >
          {{ action.title }}
        </button>
      </div>            
    </div>      
    <div
      v-if="notification?.data && showDebugData"
      class="notification-content"
    >
      <pre>{{ formatNotificationData(notification?.data) }}</pre>
    </div>
    <div
      v-if="notification?.data && showDebugData"
      class="notification-preview"
    >
      <pre>{{ formatNotificationData(notification?.data) }}</pre>
    </div>
    <div
      v-if="!notification?.data?.html_message && !notification?.data?.message && !showDebugData"
      class="notification-content empty"
    >
      {{ t('notifications.noContent', 'No content available') }}
    </div>
  </div>
</template>

<script setup>
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

defineProps({
  notification: {
    type: Object,
    default: null
  },
  showDebugData: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['action']);

const { t } = useI18n();
const router = useRouter();

const formatNotificationData = (data) => {
  if (typeof data === 'string') {
    try {
      return JSON.stringify(JSON.parse(data), null, 2);
    } catch {
      return data;
    }
  }
  if (typeof data === 'object') {
    return JSON.stringify(data, null, 2);
  }
  return String(data);
};

const handleAction = (item, action) => {
  if (action.url) {
    window.location.href = action.url;
  }
  if (action.route) {
    router.push(action.route);
  }
  // Emit action event so parent can handle closing dialog if needed
  emit('action', { item, action });
};
</script>

<style scoped lang="scss">
.notification-message {
  width: 100%;
}

.notification-content {
  color: var(--color-text);
  font-size: 0.9rem;
  line-height: 1.6;
}

.notification-content .notification-message-title {
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 16px;
  color: var(--color-text);
}

.notification-content .notification-actions {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.notification-content .notification-action-btn {
  font-size: 1rem;
  padding: 12px 24px;
  border-radius: 6px;
  border: 2px solid var(--color-green);
  background-color: var(--color-green);
  color: var(--color-bg);
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s;
  min-width: 120px;
}

.notification-content .notification-action-btn:hover {
  background-color: transparent;
  color: var(--color-green);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(53, 159, 157, 0.3);
}

.notification-content .notification-action-btn:active {
  transform: translateY(0);
  box-shadow: 0 2px 6px rgba(53, 159, 157, 0.2);
}

.notification-content .notification-action-btn:focus {
  outline: 2px solid var(--color-green);
  outline-offset: 2px;
}

.notification-content .notification-action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.notification-content pre {
  margin: 0;
  padding: 12px;
  background-color: var(--color-frame-bg);
  border-radius: 4px;
  overflow-x: auto;
  font-size: 0.85rem;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.notification-content.empty {
  color: color-mix(in srgb, var(--color-text), transparent 40%);
  font-style: italic;
}

.notification-message-title {
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 12px;
  color: var(--color-text);
}

.notification-action-btn {
  font-size: 1rem;
  padding: 6px 12px;
  border-radius: 6px;
  border: 2px solid var(--color-green);
  background-color: var(--color-green);
  color: var(--color-bg);
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s;
  min-width: 80px;
}

.notification-action-btn:hover {
  background-color: transparent;
  color: var(--color-green);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(53, 159, 157, 0.3);
}

.notification-action-btn:active {
  transform: translateY(0);
  box-shadow: 0 2px 6px rgba(53, 159, 157, 0.2);
}

.notification-action-btn:focus {
  outline: 2px solid var(--color-green);
  outline-offset: 2px;
}

.notification-action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.notification-preview {
  margin-top: 8px;
  font-size: 0.9rem;
  color: var(--color-text);
  line-height: 1.6;
}

.notification-preview pre {
  margin: 0;
  padding: 12px;
  background-color: var(--color-bg);
  border-radius: 4px;
  overflow-x: auto;
  font-size: 0.85rem;
  white-space: pre-wrap;
  word-wrap: break-word;
  max-height: 150px;
  overflow-y: auto;
}

@media (max-width: 900px) {
  .notification-content .notification-actions {
    justify-content: center;
  }

  .notification-message-title {
    font-size: 1.1rem;
    margin-bottom: 12px;
  }

  .notification-content .notification-action-btn {
    font-size: 0.9rem;
    padding: 10px 20px;
    min-width: 100px;
  }
}
</style>
